// @ts-nocheck
(function () {
  'use strict';

  function ProductShowcaseCarousel(root) {
    this.root = root;
    this.track = root.querySelector('[data-carousel-track]');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-carousel-slide]'));
    this.prevBtn = root.querySelector('[data-carousel-prev]');
    this.nextBtn = root.querySelector('[data-carousel-next]');
    this.dotsWrap = root.querySelector('[data-carousel-dots]');
    this.intervalMs = parseInt(root.dataset.slideInterval || '5000', 10);
    this.infinite = root.dataset.infinite === 'true';
    this.enableVideoSound = root.dataset.enableVideoSound === 'true';
    this.index = 0;
    this.timer = null;
    this.inView = false;
    this.hoverMode = false;
    this.hoverSlideIndex = -1;
    this.observer = null;
    this.endedHandlerMap = new WeakMap();
    this.canPlayHandlerMap = new WeakMap();

    if (!this.track || !this.slides.length) return;
    this.init();
  }

  ProductShowcaseCarousel.prototype.init = function () {
    this.root.style.setProperty('--slides-desktop', this.root.dataset.slidesToShow || '3');
    this.root.style.setProperty('--slides-tablet', this.root.dataset.slidesToShowTablet || '2');
    this.root.style.setProperty('--slides-mobile', this.root.dataset.slidesToShowMobile || '1');

    this.renderDots();
    this.bindEvents();
    this.setActive(0, false);
    this.observeInView();
  };

  ProductShowcaseCarousel.prototype.renderDots = function () {
    if (!this.dotsWrap) return;
    this.dotsWrap.innerHTML = '';

    for (const [i] of this.slides.entries()) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'product-showcase-carousel__dot';
      dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      dot.dataset.dotIndex = String(i);

      dot.addEventListener('click', () => {
        this.hoverMode = false;
        this.goTo(i, true);
      });

      this.dotsWrap.appendChild(dot);
    }
  };

  ProductShowcaseCarousel.prototype.bindEvents = function () {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.hoverMode = false;
        this.goTo(this.index - 1, true);
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.hoverMode = false;
        this.goTo(this.index + 1, true);
      });
    }

    for (const [i, slide] of this.slides.entries()) {
      slide.addEventListener('mouseenter', () => {
        this.setSlideBadgeHover(slide, true);

        if (slide.dataset.hasVideo !== 'true') return;
        this.hoverMode = true;
        this.hoverSlideIndex = i;

        if (this.isMobileScreen()) {
          this.goTo(i, true);
        }

        this.playSlideVideo(i, false);
      });

      slide.addEventListener('mouseleave', () => {
        this.setSlideBadgeHover(slide, false);

        if (this.hoverSlideIndex !== i) return;
        this.hoverMode = false;
        this.hoverSlideIndex = -1;
        this.stopVideos();
        this.runAutoplayForCurrentSlide();
      });
    }

    window.addEventListener('resize', () => {
      this.scrollToIndex(this.index, false);
    });
  };

  ProductShowcaseCarousel.prototype.observeInView = function () {
    if (!('IntersectionObserver' in window)) {
      this.inView = true;
      this.runAutoplayForCurrentSlide();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target !== this.root) continue;
          this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.95;

          if (this.inView) {
            this.runAutoplayForCurrentSlide();
          } else {
            this.clearTimer();
            this.stopVideos();
          }
        }
      },
      { threshold: [0.95] }
    );

    this.observer.observe(this.root);
  };

  ProductShowcaseCarousel.prototype.isMobileScreen = function () {
    return window.matchMedia('(max-width: 749px)').matches;
  };

  ProductShowcaseCarousel.prototype.setSlideBadgeHover = function (slide, isHovered) {
    const badge = slide.querySelector('[data-carousel-badge]');
    if (!(badge instanceof HTMLElement)) return;

    const defaultBg = badge.dataset.badgeBg || '#ffffff';
    const hoverBg = badge.dataset.badgeBgHover || '#989149';

    badge.style.backgroundColor = isHovered ? hoverBg : defaultBg;
  };

  ProductShowcaseCarousel.prototype.clearTimer = function () {
    if (!this.timer) return;
    clearTimeout(this.timer);
    this.timer = null;
  };

  ProductShowcaseCarousel.prototype.clearVideoHandlers = function (video) {
    const endedHandler = this.endedHandlerMap.get(video);
    if (endedHandler) {
      video.removeEventListener('ended', endedHandler);
      this.endedHandlerMap.delete(video);
    }

    const canPlayHandler = this.canPlayHandlerMap.get(video);
    if (canPlayHandler) {
      video.removeEventListener('canplay', canPlayHandler);
      this.canPlayHandlerMap.delete(video);
    }
  };

  ProductShowcaseCarousel.prototype.stopVideos = function (exceptIndex) {
    for (const [i, slide] of this.slides.entries()) {
      if (i === exceptIndex) continue;
      const video = slide.querySelector('video');
      if (!(video instanceof HTMLVideoElement)) continue;

      this.clearVideoHandlers(video);
      video.pause();
      video.currentTime = 0;
    }
  };

  ProductShowcaseCarousel.prototype.playVideoWithFallback = function (video, allowMutedFallback, onFailure) {
    const tryPlay = () => {
      const promise = video.play();
      if (!promise || typeof promise.catch !== 'function') return;

      promise.catch(() => {
        if (!allowMutedFallback) {
          if (typeof onFailure === 'function') onFailure();
          return;
        }

        video.muted = true;
        video.defaultMuted = true;
        video.setAttribute('muted', '');

        const fallback = video.play();
        if (fallback && typeof fallback.catch === 'function') {
          fallback.catch(() => {});
        }
      });
    };

    if (video.readyState >= 2) {
      tryPlay();
      return;
    }

    this.clearVideoHandlers(video);
    const onCanPlay = () => {
      video.removeEventListener('canplay', onCanPlay);
      this.canPlayHandlerMap.delete(video);
      tryPlay();
    };
    this.canPlayHandlerMap.set(video, onCanPlay);
    video.addEventListener('canplay', onCanPlay, { once: true });
  };

  ProductShowcaseCarousel.prototype.playSlideVideo = function (index, isAutoplay) {
    const slide = this.slides[index];
    if (!slide) return false;

    const video = slide.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return false;

    this.stopVideos(index);
    this.clearVideoHandlers(video);
    video.setAttribute('playsinline', '');

    if (isAutoplay || !this.enableVideoSound) {
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', '');

      if (isAutoplay) {
        const onEnded = () => {
          this.goTo(this.index + 1, true);
          this.runAutoplayForCurrentSlide();
        };
        this.endedHandlerMap.set(video, onEnded);
        video.addEventListener('ended', onEnded);

        this.playVideoWithFallback(video, true, () => {
          this.clearTimer();
          this.timer = window.setTimeout(() => {
            this.goTo(this.index + 1, true);
            this.runAutoplayForCurrentSlide();
          }, this.intervalMs);
        });
        return true;
      }
    } else {
      video.muted = false;
      video.defaultMuted = false;
      video.removeAttribute('muted');
    }

    this.playVideoWithFallback(video, true);
    return true;
  };

  ProductShowcaseCarousel.prototype.runAutoplayForCurrentSlide = function () {
    this.clearTimer();
    if (!this.inView || this.hoverMode) return;

    const hasVideo = this.playSlideVideo(this.index, true);
    if (hasVideo) return;

    this.stopVideos();
    this.timer = window.setTimeout(() => {
      this.goTo(this.index + 1, true);
      this.runAutoplayForCurrentSlide();
    }, this.intervalMs);
  };

  ProductShowcaseCarousel.prototype.updateControls = function () {
    if (this.prevBtn) {
      this.prevBtn.disabled = !this.infinite && this.index <= 0;
    }

    if (this.nextBtn) {
      this.nextBtn.disabled = !this.infinite && this.index >= this.slides.length - 1;
    }

    if (!this.dotsWrap) return;
    const dots = this.dotsWrap.querySelectorAll('.product-showcase-carousel__dot');
    for (const [i, dot] of dots.entries()) {
      dot.setAttribute('aria-selected', this.index === i ? 'true' : 'false');
    }
  };

  ProductShowcaseCarousel.prototype.scrollToIndex = function (index, smooth) {
    const slide = this.slides[index];
    if (!slide || !this.track) return;

    this.track.scrollTo({
      left: slide.offsetLeft,
      behavior: smooth ? 'smooth' : 'auto',
    });
  };

  ProductShowcaseCarousel.prototype.setActive = function (index, smooth) {
    this.index = index;
    this.scrollToIndex(index, smooth);
    this.updateControls();
  };

  ProductShowcaseCarousel.prototype.normalizeIndex = function (index) {
    if (!this.infinite) {
      return Math.max(0, Math.min(index, this.slides.length - 1));
    }

    if (index < 0) return this.slides.length - 1;
    if (index >= this.slides.length) return 0;
    return index;
  };

  ProductShowcaseCarousel.prototype.goTo = function (index, smooth) {
    const nextIndex = this.normalizeIndex(index);
    this.setActive(nextIndex, smooth);
  };

  function initAll() {
    document.querySelectorAll('[data-product-showcase-carousel]').forEach(function (root) {
      if (!(root instanceof HTMLElement)) return;
      if (root.__pscInstance) return;
      root.__pscInstance = new ProductShowcaseCarousel(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (!(target instanceof HTMLElement)) return;
    var root = target.querySelector('[data-product-showcase-carousel]');
    if (!root) return;
    root.__pscInstance = new ProductShowcaseCarousel(root);
  });
})();
